import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, PlusSquare, Globe } from 'lucide-react';
import { Button } from '../ui/Button';
import { useLanguage } from '../../context/LanguageContext';
export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const {
    language,
    setLanguage,
    t
  } = useLanguage();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);
  const navLinks = [{
    name: t.nav.home,
    path: '/'
  }, {
    name: t.nav.about,
    path: '/about'
  }, {
    name: t.nav.services,
    path: '/services'
  }, {
    name: t.nav.specialties,
    path: '/specialties'
  }, {
    name: t.nav.specialists,
    path: '/specialists'
  }, {
    name: t.nav.patients,
    path: '/patients'
  }];
  const isActive = (path: string) => {
    if (path === '/' && location.pathname !== '/') return false;
    return location.pathname.startsWith(path);
  };
  const isHome = location.pathname === '/';
  const isTransparent = !isScrolled && isHome;
  return <header className={`
        fixed top-0 left-0 right-0 z-40 transition-all duration-300
        ${isScrolled ? 'bg-white/95 backdrop-blur-md shadow-md py-3' : 'bg-transparent py-5'}
      `} data-id="element-67">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-68">
        <div className="flex justify-between items-center" data-id="element-69">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group" data-id="element-70">
            <div className={`
              p-2 rounded-lg transition-colors
              ${isScrolled ? 'bg-caces-navy text-white' : 'bg-white text-caces-navy'}
            `} data-id="element-71">
              <PlusSquare size={24} strokeWidth={2.5} data-id="element-72" />
            </div>
            <span className={`
              font-serif text-2xl font-bold tracking-tight
              ${isScrolled ? 'text-caces-navy' : 'text-white'}
              ${!isHome && !isScrolled ? 'text-caces-navy' : ''}
            `} data-id="element-73">
              CACES
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8" data-id="element-74">
            {navLinks.map(link => <Link key={link.path} to={link.path} className={`
                  text-sm font-medium transition-colors hover:text-caces-accent
                  ${isActive(link.path) ? 'text-caces-accent font-semibold' : ''}
                  ${isTransparent ? 'text-white/90 hover:text-white' : 'text-gray-700'}
                `} data-id="element-75">
                {link.name}
              </Link>)}

            {/* Language Toggle */}
            <div className="flex items-center bg-black/10 rounded-lg p-1 gap-1 backdrop-blur-sm" data-id="element-76">
              <button onClick={() => setLanguage('en')} className={`
                  px-2 py-1 text-xs font-bold rounded-md transition-all
                  ${language === 'en' ? 'bg-caces-accent text-white shadow-sm' : isTransparent ? 'text-white/70 hover:text-white' : 'text-gray-500 hover:text-caces-navy'}
                `} aria-label="Switch to English" data-id="element-77">
                EN
              </button>
              <button onClick={() => setLanguage('es')} className={`
                  px-2 py-1 text-xs font-bold rounded-md transition-all
                  ${language === 'es' ? 'bg-caces-accent text-white shadow-sm' : isTransparent ? 'text-white/70 hover:text-white' : 'text-gray-500 hover:text-caces-navy'}
                `} aria-label="Cambiar a Español" data-id="element-78">
                ES
              </button>
            </div>

            <Link to="/contact" data-id="element-79">
              <Button variant={isTransparent ? 'white' : 'primary'} size="sm" data-id="element-80">
                {t.nav.contact}
              </Button>
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <button className={`
              lg:hidden p-2 rounded-md transition-colors
              ${isTransparent ? 'text-white hover:bg-white/10' : 'text-caces-navy hover:bg-gray-100'}
            `} onClick={() => setIsMobileMenuOpen(true)} aria-label="Open menu" data-id="element-81">
            <Menu size={24} data-id="element-82" />
          </button>
        </div>
      </div>

      {/* Mobile Drawer */}
      <div className={`
          fixed inset-0 z-50 bg-black/50 backdrop-blur-sm transition-opacity duration-300 lg:hidden
          ${isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}
        `} onClick={() => setIsMobileMenuOpen(false)} data-id="element-83">
        <div className={`
            absolute top-0 right-0 h-full w-80 bg-white shadow-2xl transform transition-transform duration-300 ease-out
            ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}
          `} onClick={e => e.stopPropagation()} data-id="element-84">
          <div className="p-6 flex justify-between items-center border-b border-gray-100" data-id="element-85">
            <span className="font-serif text-xl font-bold text-caces-navy" data-id="element-86">
              {t.nav.menu}
            </span>
            <button onClick={() => setIsMobileMenuOpen(false)} className="p-2 text-gray-500 hover:text-caces-navy hover:bg-gray-100 rounded-md" data-id="element-87">
              <X size={24} data-id="element-88" />
            </button>
          </div>

          <div className="p-6 flex flex-col gap-4" data-id="element-89">
            {navLinks.map(link => <Link key={link.path} to={link.path} className={`
                  text-lg font-medium py-2 border-b border-gray-50
                  ${isActive(link.path) ? 'text-caces-accent' : 'text-gray-700'}
                `} data-id="element-90">
                {link.name}
              </Link>)}

            {/* Mobile Language Toggle */}
            <div className="flex items-center gap-4 py-2" data-id="element-91">
              <span className="text-gray-500 text-sm font-medium" data-id="element-92">
                Language:
              </span>
              <div className="flex bg-gray-100 rounded-lg p-1 gap-1" data-id="element-93">
                <button onClick={() => setLanguage('en')} className={`
                    px-3 py-1 text-sm font-bold rounded-md transition-all
                    ${language === 'en' ? 'bg-caces-navy text-white shadow-sm' : 'text-gray-500'}
                  `} data-id="element-94">
                  English
                </button>
                <button onClick={() => setLanguage('es')} className={`
                    px-3 py-1 text-sm font-bold rounded-md transition-all
                    ${language === 'es' ? 'bg-caces-navy text-white shadow-sm' : 'text-gray-500'}
                  `} data-id="element-95">
                  Español
                </button>
              </div>
            </div>

            <Link to="/contact" className="mt-4" data-id="element-96">
              <Button fullWidth data-id="element-97">{t.nav.contact}</Button>
            </Link>
          </div>
        </div>
      </div>
    </header>;
}